declare let _bcq: any;
import { ACTION, EVENT, LOG_LEVEL, NAME } from './constants';
import {
  PulseConfig,
  BeaconSetupDetails,
  PulseAPILogData,
  PulseLogData,
  PulseGenericLogger,
  PulseData,
} from './interfaces';

class Pulse {
  private debug: boolean;
  private context: string;

  /**
   *
   * @param setupConfig
   */
  constructor(setupConfig: PulseConfig) {
    const { beaconConfig, appContext, debugMode = false } = setupConfig;
    this.debug = debugMode;
    this.context = appContext;
    if (this.debug) {
      console.info('Pulse logger debug mode enabled');
      console.group('Pulse config setup details received: ');
      console.info(`url: ${beaconConfig.url}`);
      console.info(`bd: ${beaconConfig.bd}`);
      console.info(`bh: ${beaconConfig.bh}`);
      console.groupEnd();
    }
    this.setupPulseBeacon(beaconConfig);
    this.loadPulseBeacon(beaconConfig);
  }

  private getBeaconDetails = (
    beaconValues: BeaconSetupDetails,
    detail: 'bd' | 'bh' | 'url',
  ): string => {
    return beaconValues[detail];
  };

  private loadPulseBeacon(beaconSetupValues: BeaconSetupDetails): void {
    const script = document.createElement('script');

    script.src = this.getBeaconDetails(beaconSetupValues, 'url');

    script.async = true;

    document.head.appendChild(script);

    if (this.debug) {
      console.info(
        `Appended HEAD with url ${this.getBeaconDetails(
          beaconSetupValues,
          'url',
        )}`,
      );
    }
  }

  private setupPulseBeacon(beaconSetupValues: BeaconSetupDetails): void {
    const BD = this.getBeaconDetails(beaconSetupValues, 'bd');
    const BH = this.getBeaconDetails(beaconSetupValues, 'bh');

    const innerscriptValue = `var _bcq = _bcq || [];
    _bcq.push([
      '_setOptions',
      {
        start_time: new Date().getTime(),
        bd: '${BD}',
        bh: '${BH}'
      }
    ]);`;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    const scriptContent = document.createTextNode(innerscriptValue);
    script.appendChild(scriptContent);
    document.head.appendChild(script);
    if (this.debug) {
      console.group(`Appended script with following details to HEAD`);
      console.info(innerscriptValue.toString());
      console.groupEnd();
    }
  }

  /**
   *
   * @param dataToSend
   */
  private sendDataToPulse = (dataToSend: PulseData): void => {
    const logTimestamp = new Date().toString();
    const action = ACTION.OVERLAY;
    const data = {
      ta: {
        logTimestamp,
        userIdentifier: dataToSend.userIdentifier,
        sessionInfo: dataToSend.sessionInfo,
        ...dataToSend.data,
      },
    };

    if (this.debug) {
      console.info('Calling Pulse method: _bcq.push ');
      console.info(
        `_bcq.push(['_tagAction', ${this.context}, ${action}, ${
          dataToSend.name
        }, ${JSON.stringify(data)}])`,
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    _bcq = _bcq || [];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    _bcq.push(['_tagAction', this.context, action, dataToSend.name, data]);
  };

  /**
   *
   * @param args
   */
  public sendAPIDataToPulse = (args: PulseAPILogData): void => {
    const dataObjectSchema = {
      logLevel: args.logLevel,
      event: EVENT.API_CALL,
      eventType: args.eventType,
      details: { ...args.details },
    };

    this.sendDataToPulse({
      name: NAME.API_CALL,
      sessionInfo: args.sessionInfo,
      userIdentifier: args.userIdentifier,
      data: dataObjectSchema,
    });
  };

  /**
   *
   * @param args
   */
  public sendPageViewDataToPulse = (args: PulseLogData): void => {
    const dataObjectSchema = {
      logLevel: LOG_LEVEL.INFO,
      event: EVENT.PAGE_VIEW,
      details: { ...args.details },
    };

    this.sendDataToPulse({
      name: NAME.PAGE_VIEW,
      sessionInfo: args.sessionInfo,
      userIdentifier: args.userIdentifier,
      data: dataObjectSchema,
    });
  };

  /**
   *
   * @param args
   */
  public sendExceptionDataToPulse = (args: PulseLogData): void => {
    const dataObjectSchema = {
      logLevel: LOG_LEVEL.FATAL,
      event: EVENT.EXCEPTION,
      details: { ...args.details },
    };

    this.sendDataToPulse({
      name: NAME.EXCEPTION,
      sessionInfo: args.sessionInfo,
      userIdentifier: args.userIdentifier,
      data: dataObjectSchema,
    });
  };

  public infoLogger(args: PulseGenericLogger) {
    const dataObjectSchema = {
      logMessage: args.message,
      logLevel: LOG_LEVEL.INFO,
      event: EVENT.LOG,
      details: { ...args.details },
    };

    this.sendDataToPulse({
      name: NAME.LOG,
      sessionInfo: args.sessionInfo,
      userIdentifier: args.userIdentifier,
      data: dataObjectSchema,
    });
  }

  public warnLogger(args: PulseGenericLogger) {
    const dataObjectSchema = {
      logMessage: args.message,
      logLevel: LOG_LEVEL.WARNING,
      event: EVENT.LOG,
      details: { ...args.details },
    };

    this.sendDataToPulse({
      name: NAME.LOG,
      sessionInfo: args.sessionInfo,
      userIdentifier: args.userIdentifier,
      data: dataObjectSchema,
    });
  }

  public errorLogger(args: PulseGenericLogger) {
    const dataObjectSchema = {
      logMessage: args.message,
      logLevel: LOG_LEVEL.ERROR,
      event: EVENT.LOG,
      details: { ...args.details },
    };

    this.sendDataToPulse({
      name: NAME.LOG,
      sessionInfo: args.sessionInfo,
      userIdentifier: args.userIdentifier,
      data: dataObjectSchema,
    });
  }
}

export default Pulse;
